import { ArrowLongLeftIcon } from "@heroicons/react/20/solid";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link, useParams } from "react-router-dom";
import { isMatching, P } from "ts-pattern";

import { MeetingState, type TMeetingDetail, useGetMeetingQuery } from "~/api";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Button } from "~/components/ui/button";

import {
  EmptyDisplay,
  ExpertImage,
  ExpertName,
  formatStartDate,
  PatientName,
  StateBadge,
  ZoomButton,
} from "./meetings-shared";

const isQueryError = isMatching({ status: P.number });

// TODO: test answers of experts for every patient
// TODO: display multiple patients in tabs
export function MeetingDetail() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MobileHeader />

      <Link
        to="/pages/meetings/enrolled"
        className={clsx(
          "tw-hidden sm:tw-inline-flex",
          "tw-items-center tw-justify-center tw-gap-1 tw-text-gray-400",
          "tw-no-underline tw-transition-colors hover:tw-text-gray-500 active:tw-text-gray-500",
          "focus-visible:tw-ring-2 focus-visible:tw-ring-blue-600 focus-visible:tw-ring-offset-2",
          "tw-mr-auto",
        )}
      >
        <ArrowLongLeftIcon className="tw-h-5 tw-w-5" />
        Назад
      </Link>

      <RemoteLogErrorBoundary component="meeting-page">
        <MeetingPageContent />
      </RemoteLogErrorBoundary>
    </>
  );
}

function MobileHeader() {
  return (
    <div className="sm:tw-hidden">
      <div className="tw-relative tw-py-2">
        <Link to="/pages/meetings/enrolled">
          <ChevronLeftIcon className="tw-absolute tw-left-0 tw-top-1/2 tw-h-8 tw-w-8 -tw-translate-y-1/2 tw-text-[#34495e]" />
        </Link>

        <h2 className="tw-m-0 tw-text-center tw-text-base tw-font-semibold">Описание записи</h2>
      </div>

      <hr className="tw-my-4" />
    </div>
  );
}

function MeetingPageContent() {
  const { meeting_id } = useParams<{ meeting_id: string }>();
  const meetingQuery = useGetMeetingQuery({ meetingId: meeting_id });

  if (isQueryError(meetingQuery.error) && meetingQuery.error.status == 404) {
    return (
      <EmptyDisplay
        title="Запись не найдена"
        description="К сожалению, мы не смогли эту запись"
        link={{ to: "/pages/meetings/enrolled", children: "Перейти к моим записям" }}
      />
    );
  }

  if (meetingQuery.isError) {
    return (
      <EmptyDisplay
        title="Не удалось загрузить запись"
        description={<FormattedMessage id="events.error.description" />}
        isError
      />
    );
  }

  const meeting = meetingQuery.data;

  return (
    <>
      <div className="tw-hidden tw-items-center tw-justify-between tw-py-6 sm:tw-flex">
        {meeting ? (
          <h2 className="tw-m-0 tw-text-base tw-font-semibold">Описание записи</h2>
        ) : (
          <Skeleton width={140} className="tw-rounded-full" />
        )}

        <StateBadge meeting={meeting} short />
      </div>

      <StateBadge meeting={meeting} className="tw-float-right tw-ml-auto sm:tw-hidden" short />

      {meeting == undefined || !meeting.is_finished ? <Hr mobileOnly /> : null}

      <div className="tw-flex tw-flex-col tw-gap-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <ExpertImage expert={meeting?.expert} />
          <ExpertName meeting={meeting} />
        </div>

        {meeting == undefined || (meeting.state == MeetingState.NEW && !meeting.is_finished) ? (
          <div className="tw-flex tw-gap-3">
            <ZoomButton meeting={meeting} />
            <ActionButtons meeting={meeting} className="tw-ml-8 tw-hidden sm:tw-flex" />
          </div>
        ) : null}
      </div>

      <Hr />

      <div className="tw-flex tw-flex-col tw-gap-1">
        {meeting ? (
          <>
            <div className="tw-text-gray-400">Дата и время</div>
            <div>{formatStartDate(meeting.start_time)}</div>
          </>
        ) : (
          <>
            <Skeleton className="tw-rounded" width={200} />
            <Skeleton className="tw-rounded" width={200} />
          </>
        )}
      </div>

      <Hr />

      {meeting == undefined ? (
        <PatientInfo patient={undefined} />
      ) : (
        meeting.patients.map((patient) => (
          <PatientInfo key={patient.patient.patient_id} patient={patient} />
        ))
      )}

      {meeting == undefined || (meeting.state == MeetingState.NEW && !meeting.is_finished) ? (
        <ActionButtons meeting={meeting} className="sm:tw-hidden" />
      ) : null}
    </>
  );
}

function ActionButtons({
  meeting,
  className,
}: {
  meeting: TMeetingDetail | undefined;
  className?: string;
}) {
  return (
    <div className={clsx("tw-flex tw-gap-3", className)}>
      {meeting ? (
        <>
          {meeting.reschedule_url_client ? (
            <Button color="secondary" rounded className="max-sm:tw-uppercase" asChild>
              <a href={meeting.reschedule_url_client} target="_blank" rel="noopener noreferrer">
                Перенести запись
              </a>
            </Button>
          ) : null}

          {meeting.cancel_url_client ? (
            <Button color="danger" rounded className="max-sm:tw-uppercase" asChild>
              <a href={meeting.cancel_url_client} target="_blank" rel="noopener noreferrer">
                Отменить запись
              </a>
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <Skeleton className="tw-rounded-md tw-leading-5" width={150} height={36} />
          <Skeleton className="tw-rounded-md tw-leading-5" width={150} height={36} />
        </>
      )}
    </div>
  );
}

function PatientInfo({ patient }: { patient?: TMeetingDetail["patients"][0] }) {
  return (
    <>
      <PatientName patient={patient?.patient} />

      <hr className="tw-my-4" />

      <div className="tw-flex tw-flex-col tw-gap-1">
        {patient ? (
          <>
            <div className="tw-text-gray-400">Тема</div>
            <div>{patient.theme}</div>
          </>
        ) : (
          <>
            <Skeleton className="tw-rounded" width={200} />
            <Skeleton className="tw-rounded" width={200} />
          </>
        )}
      </div>

      <Hr />

      <div className="tw-flex tw-flex-col tw-gap-1">
        {patient ? (
          <>
            <div className="tw-text-gray-400">Вопрос</div>
            <div className="tw-whitespace-pre-wrap">{patient.question}</div>
          </>
        ) : (
          <>
            <Skeleton className="tw-rounded" width={200} />
            <Skeleton className="tw-rounded" width={200} />
          </>
        )}
      </div>

      <Hr />

      {patient == undefined || patient.expert_comment ? <Hr /> : null}

      {patient == undefined || patient.expert_comment ? (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {patient ? (
            <>
              <div className="tw-text-gray-400">Ответ эксперта</div>
              <div className="tw-whitespace-pre-wrap">{patient.expert_comment}</div>
            </>
          ) : (
            <>
              <Skeleton className="tw-rounded" width={200} />
              <Skeleton className="tw-rounded" width={200} />
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

function Hr({ mobileOnly = false }: { mobileOnly?: boolean }) {
  return (
    <hr
      className={clsx("tw-my-3 tw-border-t-transparent sm:tw-my-4", {
        "sm:tw-hidden": mobileOnly,
      })}
    />
  );
}
